import { useEffect, useState } from 'react'
import classNames from 'classnames'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import localeData from 'dayjs/plugin/localeData'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FormButtons from 'features/contracts/components/form-buttons/FormButtons'
import { dateToString, getStartDateRange, isValidEAN } from 'utils/addContract'
import parse from 'html-react-parser'
import { AddContractStepProps, AddContractSteps, MeterDataFormInputs, StartDateRange } from '../../types.ts'
import { DATE_FORMAT } from 'constants/constants'
import contractsStyles from '../../../Contracts.module.scss'
import { UserTypes } from 'store/auth/types'
import {
  Banner,
  Button,
  Checkbox,
  CheckboxList,
  Form,
  Heading,
  InlineMessage,
  Modal,
  Radio,
  RadioList,
  Select,
  TextField,
  Toggle
} from '@boltenergy-be/design-system'
import { log } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'
import { checkHasMeterAndNumberInputs } from 'utils/contracts.ts'
import 'dayjs/locale/nl'
import 'dayjs/locale/fr'
import { ContractFlowTrackingTypes } from 'types/tracking.ts'
import DocumentUpload from 'features/contracts/components/document-upload/DocumentUpload.tsx'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { ISO8601_DATE_FORMAT } from 'constants/constants.ts'
import DateField from 'components/form-fields/date-field/DateField.tsx'
import {
  CONTRACT_STATUSES,
  ContractStatus,
  COUNTER_TYPES,
  EAN_CAPTURE_TYPES,
  ENERGY_TYPES,
  EnergyType,
  LANGUAGES,
  METER_TYPES,
  REGIONS,
  SETTLEMENT_FREQUENCIES,
  SettlementFrequency,
  Situation,
  SITUATIONS,
  UTILITIES_ERROR_CODES
} from '@boltenergy-be/shared'
import backendLib from 'lib/bolt-backend'
import { ERROR_CODES_UTILITIES } from 'lib/bolt-backend/utilities/constants.ts'

// Day.js
dayjs.extend(localeData)
dayjs.extend(isBetween)

const MeterDetailsStep = ({ isMove, addContractData, setNextStep, eanLookupData }: AddContractStepProps) => {
  // REDUX STORE
  const { userType } = useStoreSelector((store) => store.auth)
  const { billingContracts } = useStoreSelector((store) => store.contact)

  // i18n
  const { t } = useTranslation(['contracts', 'validation', 'common'])
  const language = useGetLanguage()

  // React Hook Form
  const initialSituation: Situation =
    addContractData[AddContractSteps.METER_DETAILS].situation === SITUATIONS.switch && isMove
      ? SITUATIONS.move
      : addContractData[AddContractSteps.METER_DETAILS].situation
  const hookForm = useForm<MeterDataFormInputs>({
    mode: 'onBlur',
    defaultValues: {
      ...addContractData[AddContractSteps.METER_DETAILS],
      contractStartDate: addContractData[AddContractSteps.METER_DETAILS].contractStartDate ?? getStartDateRange(initialSituation).initial,
      situation: initialSituation,
      docUpload: addContractData[AddContractSteps.METER_DETAILS].docUpload ?? null
    }
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control
  } = hookForm
  const watchContractStartDate = useWatch({ control, name: 'contractStartDate' })
  const watchCounterType = useWatch({ control, name: 'counterType' })
  const watchEanCaptureType = useWatch({ control, name: 'eanCaptureType' })
  const watchElectricityEan = useWatch({ control, name: 'electricity.ean' })
  const watchElectricityExclNightEan = useWatch({ control, name: 'electricity.eanExclNight' })
  const watchGasEan = useWatch({ control, name: 'gas.ean' })
  const watchHasExclNight = useWatch({ control, name: 'hasExclNight' })
  const watchHasExclNightEan = useWatch({ control, name: 'hasExclNightEan' })
  const watchHasSolarPanels = useWatch({ control, name: 'hasSolarPanels' })
  const watchMeterType = useWatch({ control, name: 'meterType' })
  const watchSituation = useWatch({ control, name: 'situation' })
  const watchDynamicTariff = useWatch({ control, name: 'dynamicTariff' })
  const watchSettlementFrequency = useWatch({ control, name: 'settlementFrequency' })
  const { onChange: onSituationChange, ...situationField } = register('situation')

  // Local state
  const [loading, setLoading] = useState<boolean>(false)
  const [showContractStartDatePicker, setShowContractStartDatePicker] = useState<boolean>(
    !!addContractData.meterDetails.customContractStartDate
  )
  const [switchDate, setSwitchDate] = useState<string>()
  const [startDateRange, setStartDateRange] = useState<StartDateRange>(getStartDateRange(watchSituation))
  const [gasEanAtElectricityEanField, setGasEanAtElectricityEanField] = useState<boolean>(false)
  const [electricityEanAtGasEanField, setElectricityEanAtGasEanField] = useState<boolean>(false)
  const [showSettlementFrequencyModal, setShowSettlementFrequencyModal] = useState<boolean>(false)
  const [settlementFrequency, setSettlementFrequency] = useState<SettlementFrequency>(SETTLEMENT_FREQUENCIES.yearly)
  const [invalidEan, setInvalidEan] = useState<{ el: boolean; gas: boolean }>({ el: false, gas: false })

  // Constants
  const { initial, min, max } = startDateRange
  const showMeterReadAndNumberInputs = checkHasMeterAndNumberInputs({
    situation: watchSituation,
    counterType: watchCounterType,
    contractStartDate: watchContractStartDate
  })
  const { needsGas, region } = addContractData[AddContractSteps.ADDRESS]
  const isSuperUser = userType === UserTypes.SUPER_USER
  const isMeterReadingMonthVisible =
    watchHasSolarPanels && watchSituation === SITUATIONS.switch
      ? region === REGIONS.wallonia
        ? true
        : watchCounterType === COUNTER_TYPES.analog
      : false

  // Date constants
  const maxDate = switchDate && dayjs(switchDate).isAfter(max) ? switchDate : max
  const minDate = switchDate && dayjs(switchDate).isBefore(min) ? switchDate : min

  /**
   * Reset contract start date to initial when situation is new connection or switch
   */
  useEffect(() => {
    if (
      !addContractData[AddContractSteps.METER_DETAILS].contractStartDate &&
      (watchSituation === SITUATIONS.newConnection || watchSituation === SITUATIONS.switch)
    ) {
      setValue('contractStartDate', initial)
    }
  }, [initial, setValue, watchSituation, addContractData])

  /**
   * Checks if the given EAN is already used
   *
   * @param {string} ean
   * @param {EnergyType} energyType
   * @returns {boolean}
   */
  const eanIsAlreadyUsed = (ean: string, energyType: EnergyType): boolean => {
    return Object.values(billingContracts)
      .map((billingContract) => billingContract.serviceContracts[energyType === ENERGY_TYPES.gas ? 'gas' : 'electricity'])
      .some(
        (contract) =>
          !!contract &&
          (
            [
              CONTRACT_STATUSES.active,
              CONTRACT_STATUSES.effective,
              CONTRACT_STATUSES.ilcOngoing,
              CONTRACT_STATUSES.inactive,
              CONTRACT_STATUSES.rejected
            ] as ContractStatus[]
          ).includes(contract.detail.status) &&
          contract.deliveryPoint.ean === ean
      )
  }

  /**
   * Function that determines the min start date of a contract for a superuser override in case the EAN is already used by the active client.
   * @param {string} ean
   * @param {number} offsetDays
   * @returns {string}
   */
  const getMinSuperuserDateForContractWithActiveEan = (ean: string, offsetDays?: number): string => {
    const contractForEanStartDate = Object.values(billingContracts).find((billingContract) => {
      const electricityContract = billingContract.serviceContracts.electricity
      const gasContract = billingContract.serviceContracts.gas

      return (
        (electricityContract && electricityContract.deliveryPoint.ean === ean) || (gasContract && gasContract.deliveryPoint.ean === ean)
      )
    })?.serviceContracts.electricity.detail.contractualStartDate

    return dayjs(contractForEanStartDate)
      .add(offsetDays ?? 0, 'day')
      .format(ISO8601_DATE_FORMAT)
  }

  /**
   * Handles the new contract start date
   * @param {Situation} situation
   */
  const handleNewContractStartDate = (situation: Situation) => {
    const newStartDateRange = getStartDateRange(situation)
    setStartDateRange(newStartDateRange)
    setValue('contractStartDate', newStartDateRange.initial)
  }

  /**
   * Handles the submit after validation by React Hook Form
   * @param {MeterDataFormInputs} data
   */
  const onSubmit = async (data: MeterDataFormInputs) => {
    setLoading(true)
    setSwitchDate('')

    try {
      // Only check EAN data if the user knows the meter details
      const contractStartDate = dayjs(data.contractStartDate).format(ISO8601_DATE_FORMAT)

      // Fetch the EAN data
      const [electricityEanData, gasEanData] = await Promise.all([
        backendLib.getEanData({ energyType: ENERGY_TYPES.electricity, ean: data.electricity.ean.toString(), contractStartDate }),
        ...(needsGas && data.gas?.ean
          ? [backendLib.getEanData({ energyType: ENERGY_TYPES.gas, ean: data.gas.ean.toString(), contractStartDate })]
          : [])
      ])

      // Check before other scenario's as this will result in a not success for `electricityEanData?.success`
      const invalidEanCodeFromBackend = [gasEanData?.errorCode, electricityEanData?.errorCode].includes(
        UTILITIES_ERROR_CODES.eanNotFoundOnMarket
      )
      if (invalidEanCodeFromBackend) {
        setInvalidEan({
          el: electricityEanData?.errorCode === UTILITIES_ERROR_CODES.eanNotFoundOnMarket,
          gas: gasEanData?.errorCode === UTILITIES_ERROR_CODES.eanNotFoundOnMarket
        })
        return
      }

      if (electricityEanData?.success) {
        // Four scenarios to be blocked
        // 1. Gas EAN filled in at electricity
        const gasEanAtElec = electricityEanData.data?.energyType === ENERGY_TYPES.gas
        // 2. Electricity EAN filled in at gas
        const elecEanAtGas = gasEanData?.data?.energyType === ENERGY_TYPES.electricity
        // 3. Invalid contract start date & no document uploaded
        const contractStartDateIsInvalid =
          electricityEanData.data?.invalidContractStartDate && (!data.docUpload || data.docUpload?.length === 0)
        const invalidEanCodeFromBackend = [gasEanData?.errorCode, electricityEanData?.errorCode].includes(ERROR_CODES_UTILITIES.invalidEan)
        setInvalidEan({
          el: electricityEanData?.errorCode === ERROR_CODES_UTILITIES.invalidEan,
          gas: gasEanData?.errorCode === ERROR_CODES_UTILITIES.invalidEan
        })

        setGasEanAtElectricityEanField(gasEanAtElec)
        setElectricityEanAtGasEanField(elecEanAtGas)
        if (electricityEanData?.data?.switchDate && contractStartDateIsInvalid) {
          setSwitchDate(electricityEanData?.data?.switchDate)
          if (!isMove) {
            setValue('situation', SITUATIONS.move)
          }
          // Scroll to top (where form is located)
          window.scrollTo(0, 0)
        }

        if (!gasEanAtElec && !elecEanAtGas && !contractStartDateIsInvalid && !invalidEanCodeFromBackend) {
          saveDataAndGoToNextStep(data)
        }
      } else {
        saveDataAndGoToNextStep(data)
      }
    } catch (error) {
      log({ error: error as Error, identifier: `[${Flow.CONTRACT}:submitMeterDetails]` })
    } finally {
      setLoading(false)
    }
  }

  /**
   * Saves the data & goes to the next step
   * @param {MeterDataFormInputs} data
   */
  const saveDataAndGoToNextStep = (data: MeterDataFormInputs) => {
    if (showContractStartDatePicker) {
      // Add custom key to data object to save "custom contract start date" state
      data.customContractStartDate = true
    } else {
      delete data.customContractStartDate
    }

    // Delete docUpload if situation is not move or if situation is move but no doc is selected
    if (data.situation !== SITUATIONS.move || (data.situation === SITUATIONS.move && data.docUpload?.length === 0)) {
      delete data.docUpload
    }

    setNextStep(data, AddContractSteps.CUSTOMER_DATA)
  }

  /**
   * Updates the Ean Capture Type input to "Overwritten by customer"
   */
  const updateEanCaptureTypeToOverwrittenIfNecessary = () => {
    if (watchEanCaptureType === EAN_CAPTURE_TYPES.prefilled) {
      setValue('eanCaptureType', EAN_CAPTURE_TYPES.overwritten)
    }
  }

  /**
   * Handles the meter reading month select
   * @param {number} selectedMonthIndex
   */
  const handleMeterReadingMonthSelectChange = (selectedMonthIndex: number): void => {
    if (!isNaN(selectedMonthIndex)) {
      // Define the start date
      let startDate = dayjs()
        .month(selectedMonthIndex - 1)
        .date(15)
        .hour(12)

      // Check if start date is in the future and add a year if not
      if (startDate.isBefore(dayjs())) startDate = startDate.add(1, 'year')

      // Show the contract start date picker
      setShowContractStartDatePicker(true)

      // Set the value in RHF values
      setValue('contractStartDate', startDate.toISOString())
    }
  }

  /**
   * Add EAN lookup data to form data
   */
  useEffect(() => {
    if (eanLookupData) {
      const { electricity, gas } = eanLookupData

      if (electricity || gas) {
        setValue('eanCaptureType', EAN_CAPTURE_TYPES.prefilled)

        if (electricity) setValue('electricity.ean', electricity)
        if (gas) setValue('gas.ean', gas)
      } else {
        setValue('eanCaptureType', EAN_CAPTURE_TYPES.notFound)
      }
    }
  }, [eanLookupData])

  return (
    <section>
      <Heading as="h1" variant="h4" className="mb-300">
        {t(($) => $.add.steps.meterDetails.title)}
      </Heading>

      <FormProvider {...hookForm}>
        <Form onSubmit={handleSubmit(onSubmit)} className={contractsStyles['narrow-grid']}>
          <Modal
            isOpen={showSettlementFrequencyModal}
            onRequestClose={() => setShowSettlementFrequencyModal(false)}
            title={t(($) => $.add.steps.meterDetails.yearlyOrMonthlyModal.title)}
          >
            <p>{t(($) => $.add.steps.meterDetails.yearlyOrMonthlyModal.description1)}</p>
            <p>{parse(t(($) => $.add.steps.meterDetails.yearlyOrMonthlyModal.description2))}</p>
            <p>{t(($) => $.add.steps.meterDetails.yearlyOrMonthlyModal.description3)}</p>
            <fieldset>
              <legend className={contractsStyles['fieldset-legend']}>
                {t(($) => $.add.steps.meterDetails.yearlyOrMonthlyModal.howOftenField.label)}
              </legend>
              <Toggle
                isFullwidth
                active={settlementFrequency}
                onClick={(value) => setSettlementFrequency(value)}
                options={[
                  {
                    label: t(($) => $.periodicity.yearly, {
                      ns: 'common'
                    }),
                    value: SETTLEMENT_FREQUENCIES.yearly
                  },
                  {
                    label: t(($) => $.periodicity.monthly, {
                      ns: 'common'
                    }),
                    value: SETTLEMENT_FREQUENCIES.monthly
                  }
                ]}
              />
            </fieldset>
            <Button
              onClick={() => {
                setShowSettlementFrequencyModal(false)
                setValue('settlementFrequency', settlementFrequency)
              }}
            >
              {t(($) => $.add.steps.meterDetails.yearlyOrMonthlyModal.howOftenField.confirm)}
            </Button>
          </Modal>

          {/* MY SITUATION */}
          {isMove ? (
            <fieldset className="grid-col-full">
              <legend className={contractsStyles['fieldset-legend']}>{t(($) => $.add.steps.meterDetails.form.situation.moveLabel)}</legend>
              <Toggle
                isFullwidth
                active={watchSituation}
                options={[
                  {
                    value: SITUATIONS.move,
                    label: t(($) => $.yes, {
                      ns: 'common'
                    })
                  },
                  {
                    value: SITUATIONS.newConnection,
                    label: t(($) => $.no, {
                      ns: 'common'
                    })
                  }
                ]}
                onClick={(value) => {
                  setValue('situation', value)
                  handleNewContractStartDate(value)
                }}
              />
            </fieldset>
          ) : (
            <RadioList label={t(($) => $.add.steps.meterDetails.form.situation.addLabel)}>
              {Object.values(SITUATIONS).map((situation) => (
                <Radio
                  {...situationField}
                  id={situation}
                  key={situation}
                  value={situation}
                  label={t(($) => $.add.steps.meterDetails.form.situation.options[situation])}
                  onChange={(e) => {
                    void onSituationChange(e)
                    handleNewContractStartDate(e.currentTarget.value as Situation)
                  }}
                />
              ))}
            </RadioList>
          )}

          {/* METER READING MONTH */}
          {isMeterReadingMonthVisible && (
            <>
              <Select
                label={t(($) => $.add.steps.meterDetails.form.meterReadingMonth.label)}
                id="meterReadingMonth"
                {...register('meterReadingMonth', {
                  required: t(($) => $.required, {
                    ns: 'validation'
                  })
                })}
                onChange={(e) => handleMeterReadingMonthSelectChange(parseInt(e.currentTarget.value))}
                error={errors.meterReadingMonth?.message}
              >
                <option value="" disabled>
                  {t(($) => $.add.steps.meterDetails.form.meterReadingMonth.select)}
                </option>
                {dayjs.months().map((month, index) => (
                  <option key={index} value={index + 1}>
                    {month}
                  </option>
                ))}
              </Select>

              <InlineMessage className="grid-col-full" type="informative">
                {t(($) => $.add.steps.meterDetails.form.meterReadingMonth.explanation)}
              </InlineMessage>
            </>
          )}

          <fieldset>
            <legend className={contractsStyles['fieldset-legend']}>
              {t(($) => $.add.steps.meterDetails.form.contractStartDate.label)}
            </legend>

            {/* CONTRACT START DATE */}
            {!showContractStartDatePicker || watchSituation === SITUATIONS.newConnection ? (
              <p className={contractsStyles.description}>
                {watchSituation === SITUATIONS.newConnection ? (
                  t(($) => $.add.steps.meterDetails.form.contractStartDate.values.newConnection)
                ) : (
                  <>
                    {dateToString(initial)}
                    <Button
                      type="button"
                      representation="link"
                      className={contractsStyles.link}
                      onClick={() => setShowContractStartDatePicker(true)}
                    >
                      {t(($) => $.edit, {
                        ns: 'common'
                      })}
                    </Button>
                  </>
                )}
              </p>
            ) : (
              <div>
                <DateField
                  hookFormKey="contractStartDate"
                  label={t(($) => $.add.steps.meterDetails.form.contractStartDate.label)}
                  hiddenLabel
                  min={
                    isSuperUser && eanIsAlreadyUsed(watchElectricityEan, ENERGY_TYPES.electricity)
                      ? getMinSuperuserDateForContractWithActiveEan(watchElectricityEan)
                      : dayjs(minDate).format(ISO8601_DATE_FORMAT)
                  }
                  max={dayjs(maxDate).format(ISO8601_DATE_FORMAT)}
                  defaultValue={watchContractStartDate || dayjs(initial).format(ISO8601_DATE_FORMAT)}
                  placeholder={t(($) => $.add.steps.meterDetails.form.contractStartDate.placeholder)}
                />

                <Button
                  type="button"
                  representation="link"
                  className={classNames(contractsStyles.link, contractsStyles['no-margin'], 'mt-200')}
                  onClick={() => {
                    setValue('contractStartDate', initial) // Reset contract start date back to initial
                    setShowContractStartDatePicker(false)
                  }}
                >
                  {t(($) => $.add.steps.meterDetails.form.contractStartDate.standard)}
                </Button>
              </div>
            )}

            {watchSituation === SITUATIONS.move && showContractStartDatePicker && switchDate !== watchContractStartDate && (
              <Banner type="informative" className="mt-400">
                {t(($) => $.add.steps.meterDetails.form.contractStartDate.attentionMove)}
              </Banner>
            )}

            {switchDate && switchDate !== watchContractStartDate && (
              <div className="mt-400">
                <span className="help-block text-negative">
                  {t(($) => $.add.steps.meterDetails.form.contractStartDate.wrongSwitchDate, {
                    interpolation: { escapeValue: false },
                    date: dayjs(switchDate).format(DATE_FORMAT)
                  })}
                </span>
              </div>
            )}
          </fieldset>

          {/* DOC UPLOAD */}
          {watchSituation === SITUATIONS.move && <DocumentUpload />}

          {/* HAS SOLAR PANELS */}
          <fieldset>
            <legend className={contractsStyles['fieldset-legend']}>{t(($) => $.add.steps.meterDetails.form.hasSolarPanels)}</legend>

            <Toggle
              isFullwidth
              active={watchHasSolarPanels}
              options={[
                {
                  value: true,
                  label: t(($) => $.yes, {
                    ns: 'common'
                  })
                },
                {
                  value: false,
                  label: t(($) => $.no, {
                    ns: 'common'
                  })
                }
              ]}
              onClick={(value) => setValue('hasSolarPanels', value)}
            />
          </fieldset>

          {/* COUNTER TYPE */}
          <fieldset>
            <legend className={contractsStyles['fieldset-legend']}>{t(($) => $.add.steps.meterDetails.form.counterType.label)}</legend>
            <Toggle
              isFullwidth
              active={watchCounterType}
              options={[
                {
                  value: COUNTER_TYPES.digital,
                  label: t(($) => $.yes, {
                    ns: 'common'
                  })
                },
                {
                  value: COUNTER_TYPES.analog,
                  label: t(($) => $.no, {
                    ns: 'common'
                  })
                }
              ]}
              onClick={(value) => {
                setValue('counterType', value)
                if (value === COUNTER_TYPES.analog) {
                  // Monthly is only possible when the meter is digital so make sure to reset to yearly
                  setSettlementFrequency(SETTLEMENT_FREQUENCIES.yearly)
                  setValue('settlementFrequency', SETTLEMENT_FREQUENCIES.yearly)

                  setValue('dynamicTariff', false)
                  if (addContractData['address'].region === REGIONS.wallonia) {
                    setValue('walloniaSolarPanelsSince2024', false)
                  }
                }
              }}
            />
          </fieldset>

          {[UserTypes.SUPER_USER].some((type) => type === userType) &&
            watchCounterType === COUNTER_TYPES.digital &&
            region === REGIONS.flanders && (
              <p className="grid-col-full">
                {t(($) => $.add.steps.meterDetails.form.counterType.descriptions[watchSettlementFrequency])}{' '}
                <Button
                  representation="link"
                  onClick={(e) => {
                    e.preventDefault()
                    setShowSettlementFrequencyModal(true)
                  }}
                >
                  {t(($) => $.add.steps.meterDetails.form.counterType.clickHere)}
                </Button>
              </p>
            )}

          {/* DYNAMIC TARIFF */}
          {watchCounterType === COUNTER_TYPES.digital && addContractData['address'].region === REGIONS.flanders && !watchHasExclNight && (
            <fieldset>
              <legend className={contractsStyles['fieldset-legend']}>
                {t(($) => $.add.steps.meterDetails.form.dynamicTariff.label, {
                  defaultValue: 'Wil je graag dynamische prijzen?'
                })}
              </legend>

              <Toggle
                isFullwidth
                className="mb-400"
                active={watchDynamicTariff}
                options={[
                  {
                    label: t(($) => $.yes, {
                      defaultValue: 'Ja',
                      ns: 'common'
                    }),
                    value: true
                  },
                  {
                    label: t(($) => $.no, {
                      defaultValue: 'Nee',
                      ns: 'common'
                    }),
                    value: false
                  }
                ]}
                onClick={(value) => setValue('dynamicTariff', value)}
              />
              <p>
                {t(($) => $.add.steps.meterDetails.form.dynamicTariff.description, {
                  defaultValue:
                    'Met de optie dynamische prijzen zal je elektriciteitsverbruik volgens de marktprijs per uur afgerekend worden. Door je energieverbruik goed te plannen, kan je besparen. Je kan dit op elk moment stopzetten.'
                })}{' '}
                <a
                  href={
                    language === LANGUAGES.NL
                      ? 'https://boltenergie.be/dynamische-prijzen'
                      : 'https://www.boltenergie.be/fr/prix-dynamiques'
                  }
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {t(($) => $.readMore, {
                    ns: 'common'
                  })}
                  .
                </a>
              </p>
            </fieldset>
          )}

          <hr className={classNames(contractsStyles.divider, 'grid-col-full')} />

          <Heading as="h3" variant="h4" className="grid-col-full">
            {t(($) => $.add.steps.meterDetails.form.electricity.title)}
          </Heading>

          <CheckboxList label="">
            {/* ELECTRICITY METER TYPE */}
            <Checkbox
              name="meterType"
              label={t(($) => $.add.steps.meterDetails.form.electricity.meterType)}
              id="meterType"
              checked={watchMeterType === METER_TYPES.double}
              onChange={(e) => setValue('meterType', e.currentTarget.checked ? METER_TYPES.double : METER_TYPES.single)}
            />

            {/* HAS EXCLUSIVE NIGHT METER (LOCAL STATE) */}
            <Checkbox
              {...register('hasExclNight')}
              label={t(($) => $.add.steps.meterDetails.form.electricity.hasExclNight)}
              id="hasExclNight"
            />

            {/* WALLONIA SOLAR PANELS INSTALLED SINCE 2024*/}
            {addContractData['address'].region === REGIONS.wallonia &&
              watchCounterType === COUNTER_TYPES.digital &&
              watchHasSolarPanels && (
                <Checkbox
                  {...register('walloniaSolarPanelsSince2024')}
                  label={t(($) => $.add.steps.meterDetails.form.electricity.walloniaSolarPanelsSince2024)}
                  id="walloniaSolarPanelsSince2024"
                />
              )}
          </CheckboxList>

          {/* ELECTRICITY EAN */}
          <TextField
            id="electricityEan"
            label={t(($) => $.add.steps.meterDetails.form.electricity.EAN)}
            placeholder="54144..."
            inputMode="numeric"
            {...register('electricity.ean', {
              required: t(($) => $.required, {
                ns: 'validation'
              }),
              validate: {
                valid: (value) =>
                  isValidEAN(value) ||
                  t(($) => $.invalid.EAN, {
                    ns: 'validation'
                  }),
                ...(needsGas && {
                  notSameAsGas: (value, { gas }) =>
                    value !== gas?.ean ||
                    t(($) => $.invalid.EANSameForElectricityAndGas, {
                      ns: 'validation'
                    })
                }),
                ...(!isSuperUser && {
                  notAlreadyUsed: (value) =>
                    !eanIsAlreadyUsed(value, ENERGY_TYPES.electricity) ||
                    t(($) => $.invalid.EANAlreadyUsed, {
                      ns: 'validation'
                    })
                })
              }
            })}
            error={
              errors?.electricity?.ean?.message ||
              (gasEanAtElectricityEanField
                ? t(($) => $.invalid.gasEanAtElectricityEanField, {
                    ns: 'validation'
                  })
                : invalidEan.el
                  ? t(($) => $.invalid.EAN, {
                      ns: 'validation'
                    })
                  : undefined)
            }
            onChange={() => {
              setInvalidEan({
                ...invalidEan,
                el: false
              })
              setGasEanAtElectricityEanField(false)
              updateEanCaptureTypeToOverwrittenIfNecessary()
            }}
          />
          {watchEanCaptureType === EAN_CAPTURE_TYPES.prefilled && !!watchElectricityEan && (
            <InlineMessage type="positive" className="grid-col-full">
              {t(($) => $.add.steps.meterDetails.form.eanPrefilled)}
            </InlineMessage>
          )}
          {isSuperUser && eanIsAlreadyUsed(watchElectricityEan, ENERGY_TYPES.electricity) && (
            <InlineMessage type="warning" className="grid-col-full">
              {t(($) => $.invalid.EANAlreadyUsed, {
                ns: 'validation'
              })}
            </InlineMessage>
          )}

          {showMeterReadAndNumberInputs && (
            <>
              {watchMeterType === METER_TYPES.single ? (
                <>
                  {/* ELECTRICITY METER READING TOTAL (SINGLE RATE) */}
                  <TextField
                    id="electricityTotalSingle"
                    label={t(($) => $.add.steps.meterDetails.form.electricity.total)}
                    placeholder="123456"
                    inputMode="numeric"
                    {...register('electricity.total', {
                      required: t(($) => $.required, {
                        ns: 'validation'
                      }),
                      validate: {
                        isNotEan: (value) =>
                          (!!value && !isValidEAN(value.toString(10))) ||
                          t(($) => $.invalid.looksLikeEan, {
                            ns: 'validation'
                          }),
                        valid: (value) =>
                          (!!value && value > 0) ||
                          t(($) => $.invalid.meterReading, {
                            ns: 'validation'
                          })
                      }
                    })}
                    suffix={{ as: ',000', className: contractsStyles.bordered }}
                    error={errors?.electricity?.total?.message}
                  />
                </>
              ) : (
                <>
                  {/* ELECTRICITY METER READING DAY (DOUBLE RATE) */}
                  <TextField
                    id="electricityTotalDoubleDay"
                    label={t(($) => $.add.steps.meterDetails.form.electricity.day)}
                    inputMode="numeric"
                    {...register('electricity.day', {
                      required: t(($) => $.required, {
                        ns: 'validation'
                      }),
                      validate: {
                        isNotEan: (value) =>
                          (!!value && !isValidEAN(value.toString(10))) ||
                          t(($) => $.invalid.looksLikeEan, {
                            ns: 'validation'
                          }),
                        valid: (value) =>
                          (!!value && value > 0) ||
                          t(($) => $.invalid.meterReading, {
                            ns: 'validation'
                          })
                      }
                    })}
                    suffix={{ as: ',000', className: contractsStyles.bordered }}
                    error={errors?.electricity?.day?.message}
                  />

                  {/* ELECTRICITY METER READING NIGHT (DOUBLE RATE) */}
                  <TextField
                    id="electricityTotalDoubleNight"
                    label={t(($) => $.add.steps.meterDetails.form.electricity.night)}
                    inputMode="numeric"
                    {...register('electricity.night', {
                      required: t(($) => $.required, {
                        ns: 'validation'
                      }),
                      validate: {
                        isNotEan: (value) =>
                          (!!value && !isValidEAN(value.toString(10))) ||
                          t(($) => $.invalid.looksLikeEan, {
                            ns: 'validation'
                          }),
                        valid: (value) =>
                          (!!value && value > 0) ||
                          t(($) => $.invalid.meterReading, {
                            ns: 'validation'
                          })
                      }
                    })}
                    suffix={{ as: ',000', className: contractsStyles.bordered }}
                    error={errors?.electricity?.night?.message}
                  />
                </>
              )}

              {/* ELECTRICITY METER NUMBER */}
              <TextField
                id="electricityMeterNumber"
                label={t(($) => $.add.steps.meterDetails.form.electricity.meterNumber)}
                inputMode="numeric"
                {...register('electricity.meterNumber', {
                  required: t(($) => $.required, {
                    ns: 'validation'
                  }),
                  validate: {
                    valid: (value) =>
                      (!!value && !isValidEAN(value)) ||
                      t(($) => $.invalid.looksLikeEan, {
                        ns: 'validation'
                      })
                  }
                })}
                error={errors?.electricity?.meterNumber?.message}
              />
            </>
          )}

          {watchHasExclNight && (
            <>
              <Checkbox
                {...register('hasExclNightEan')}
                label={t(($) => $.add.steps.meterDetails.form.electricity.hasExclNightEanNumber)}
                id="hasExclNightEan"
                className="grid-col-full"
              />

              {/* ELECTRICITY EXCL. NIGHT EAN NUMBER */}
              {watchHasExclNightEan && (
                <>
                  <TextField
                    id="electricityEanExclNight"
                    label={t(($) => $.add.steps.meterDetails.form.electricity.exclEAN)}
                    placeholder="54144..."
                    {...register('electricity.eanExclNight', {
                      required: t(($) => $.required, {
                        ns: 'validation'
                      }),
                      validate: {
                        valid: (value) =>
                          isValidEAN(value!) ||
                          t(($) => $.invalid.EAN, {
                            ns: 'validation'
                          }),
                        ...(!isSuperUser && {
                          notAlreadyUsed: (value) =>
                            !eanIsAlreadyUsed(value!, ENERGY_TYPES.electricity) ||
                            t(($) => $.invalid.EANAlreadyUsed, {
                              ns: 'validation'
                            })
                        })
                      }
                    })}
                    error={errors?.electricity?.eanExclNight?.message}
                  />
                  {isSuperUser && eanIsAlreadyUsed(watchElectricityExclNightEan!, ENERGY_TYPES.electricity) && (
                    <InlineMessage type="warning">
                      {t(($) => $.invalid.EANAlreadyUsed, {
                        ns: 'validation'
                      })}
                    </InlineMessage>
                  )}
                </>
              )}

              {/* ELECTRICITY METER EXCL. NIGHT */}
              {showMeterReadAndNumberInputs && (
                <TextField
                  id="electricityTotalExclNight"
                  label={t(($) => $.add.steps.meterDetails.form.electricity.exclNight)}
                  inputMode="numeric"
                  {...register('electricity.exclNight', {
                    required: t(($) => $.required, {
                      ns: 'validation'
                    }),
                    validate: {
                      isNotEan: (value) =>
                        (!!value && !isValidEAN(value.toString(10))) ||
                        t(($) => $.invalid.looksLikeEan, {
                          ns: 'validation'
                        }),
                      valid: (value) =>
                        (!!value && value > 0) ||
                        t(($) => $.invalid.meterReading, {
                          ns: 'validation'
                        })
                    }
                  })}
                  suffix={{ as: ',000', className: contractsStyles.bordered }}
                  error={errors?.electricity?.exclNight?.message}
                />
              )}
            </>
          )}

          {needsGas && (
            <>
              <hr className={classNames(contractsStyles.divider, 'grid-col-full')} />

              <Heading as="h3" variant="h4" className="grid-col-full">
                {t(($) => $.add.steps.meterDetails.form.gas.title)}
              </Heading>

              {/* GAS EAN */}
              <TextField
                id="gas.ean"
                label={t(($) => $.add.steps.meterDetails.form.gas.EAN)}
                placeholder="54144..."
                {...register('gas.ean', {
                  required: t(($) => $.required, {
                    ns: 'validation'
                  }),
                  validate: {
                    valid: (value) =>
                      isValidEAN(value!) ||
                      t(($) => $.invalid.EAN, {
                        ns: 'validation'
                      }),
                    notSameAsElectricity: (value, { electricity }) =>
                      value !== electricity.ean ||
                      t(($) => $.invalid.EANSameForElectricityAndGas, {
                        ns: 'validation'
                      }),
                    ...(!isSuperUser && {
                      notAlreadyUsed: (value) =>
                        !eanIsAlreadyUsed(value!, ENERGY_TYPES.gas) ||
                        t(($) => $.invalid.EANAlreadyUsed, {
                          ns: 'validation'
                        })
                    })
                  }
                })}
                error={
                  errors?.gas?.ean?.message ||
                  (electricityEanAtGasEanField
                    ? t(($) => $.invalid.electricityEanAtGasEanField, {
                        ns: 'validation'
                      })
                    : invalidEan.gas
                      ? t(($) => $.invalid.EAN, {
                          ns: 'validation'
                        })
                      : undefined)
                }
                onChange={() => {
                  setInvalidEan({
                    ...invalidEan,
                    gas: false
                  })
                  setElectricityEanAtGasEanField(false)
                  updateEanCaptureTypeToOverwrittenIfNecessary()
                }}
              />
              {watchEanCaptureType === EAN_CAPTURE_TYPES.prefilled && !!watchGasEan && (
                <InlineMessage type="positive" className="grid-col-full">
                  {t(($) => $.add.steps.meterDetails.form.eanPrefilled)}
                </InlineMessage>
              )}
              {isSuperUser && eanIsAlreadyUsed(watchGasEan!, ENERGY_TYPES.gas) && (
                <InlineMessage type="warning" className="grid-col-full">
                  {t(($) => $.invalid.EANAlreadyUsed, {
                    ns: 'validation'
                  })}
                </InlineMessage>
              )}

              {showMeterReadAndNumberInputs && (
                <>
                  {/* GAS METER READING TOTAL */}
                  <TextField
                    id="gasTotal"
                    label={t(($) => $.add.steps.meterDetails.form.gas.total)}
                    inputMode="numeric"
                    {...register('gas.total', {
                      required: t(($) => $.required, {
                        ns: 'validation'
                      }),
                      validate: {
                        isNotEan: (value) =>
                          (!!value && !isValidEAN(value.toString(10))) ||
                          t(($) => $.invalid.looksLikeEan, {
                            ns: 'validation'
                          }),
                        valid: (value) =>
                          (!!value && value > 0) ||
                          t(($) => $.invalid.meterReading, {
                            ns: 'validation'
                          })
                      }
                    })}
                    suffix={{ as: ',000', className: contractsStyles.bordered }}
                    error={errors?.gas?.total?.message}
                  />

                  {/* GAS METER NUMBER */}
                  <TextField
                    id="gasMeterNumber"
                    label={t(($) => $.add.steps.meterDetails.form.gas.meterNumber)}
                    inputMode="numeric"
                    {...register('gas.meterNumber', {
                      required: t(($) => $.required, {
                        ns: 'validation'
                      }),
                      validate: {
                        valid: (value) =>
                          (!!value && !isValidEAN(value)) ||
                          t(($) => $.invalid.looksLikeEan, {
                            ns: 'validation'
                          })
                      }
                    })}
                    error={errors?.gas?.meterNumber?.message}
                  />
                </>
              )}
            </>
          )}

          <FormButtons trackingId={ContractFlowTrackingTypes.ADD} currentStep={AddContractSteps.METER_DETAILS} loading={loading} />
        </Form>
      </FormProvider>
    </section>
  )
}

export default MeterDetailsStep
